/**
 * Native Notification Service
 * 
 * Handles native push notifications and local notifications using Capacitor plugins.
 * Works on iOS, Android, and falls back to Web Push API for browsers.
 * 
 * Features:
 * - Local notifications for appointment reminders
 * - Schedule notifications for future times
 * - Cancel/update scheduled notifications
 * - Deep link handling for notification actions
 * - Graceful web fallback
 */

import { Capacitor } from '@capacitor/core';

// Dynamic imports for native plugins (only available in native builds)
let LocalNotifications = null;
let PushNotifications = null;

// Notification channel IDs for Android
export const NOTIFICATION_CHANNELS = {
  APPOINTMENTS: 'appointments',
  REMINDERS: 'reminders',
  ALERTS: 'alerts',
};

// Notification types
export const NOTIFICATION_TYPE = {
  APPOINTMENT_REMINDER: 'appointment_reminder',
  MEDICATION_REMINDER: 'medication_reminder',
  DOCUMENT_EXPIRING: 'document_expiring',
  PARKING_EXPIRING: 'parking_expiring',
  IMPORTANT_DATE: 'important_date',
};

/**
 * Initialize notification plugins
 */
const initNotificationPlugins = async () => {
  if (!Capacitor.isNativePlatform()) {
    console.log('[Notifications] Web platform - using Web Push API');
    return false;
  }
  
  try {
    if (!LocalNotifications) {
      const localModule = await import('@capacitor/local-notifications');
      LocalNotifications = localModule.LocalNotifications;
    }
    
    if (!PushNotifications) {
      const pushModule = await import('@capacitor/push-notifications');
      PushNotifications = pushModule.PushNotifications;
    }
    
    return true;
  } catch (error) {
    console.error('[Notifications] Failed to load plugins:', error);
    return false;
  }
};

/**
 * Check if native notifications are available
 */
export const isNativeNotificationAvailable = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  const isNative = await initNotificationPlugins();
  
  if (isNative && LocalNotifications) {
    try {
      const result = await LocalNotifications.requestPermissions();
      return {
        granted: result.display === 'granted',
        status: result.display,
      };
    } catch (error) {
      console.error('[Notifications] Permission error:', error);
      return { granted: false, status: 'error' };
    }
  }
  
  // Web fallback
  if ('Notification' in window) {
    const permission = await Notification.requestPermission();
    return {
      granted: permission === 'granted',
      status: permission,
    };
  }
  
  return { granted: false, status: 'unsupported' };
};

/**
 * Check current notification permission status
 */
export const checkNotificationPermission = async () => {
  const isNative = await initNotificationPlugins();
  
  if (isNative && LocalNotifications) {
    try {
      const result = await LocalNotifications.checkPermissions();
      return {
        granted: result.display === 'granted',
        status: result.display,
      };
    } catch (error) {
      return { granted: false, status: 'error' };
    }
  }
  
  // Web fallback
  if ('Notification' in window) {
    return {
      granted: Notification.permission === 'granted',
      status: Notification.permission,
    };
  }
  
  return { granted: false, status: 'unsupported' };
};

/**
 * Create notification channels for Android
 */
export const setupNotificationChannels = async () => {
  const isNative = await initNotificationPlugins();
  
  if (!isNative || !LocalNotifications) return;
  
  try {
    // Android requires notification channels
    if (Capacitor.getPlatform() === 'android') {
      await LocalNotifications.createChannel({
        id: NOTIFICATION_CHANNELS.APPOINTMENTS,
        name: 'Appointment Reminders',
        description: 'Reminders for upcoming medical appointments',
        importance: 4, // HIGH
        visibility: 0, // PRIVATE
        sound: 'default',
        vibration: true,
        lights: true,
      });
      
      await LocalNotifications.createChannel({
        id: NOTIFICATION_CHANNELS.REMINDERS,
        name: 'General Reminders',
        description: 'Reminders for documents, dates, and other events',
        importance: 3, // DEFAULT
        sound: 'default',
        vibration: true,
      });
      
      await LocalNotifications.createChannel({
        id: NOTIFICATION_CHANNELS.ALERTS,
        name: 'Urgent Alerts',
        description: 'Time-sensitive alerts like parking expiration',
        importance: 5, // MAX
        sound: 'default',
        vibration: true,
        lights: true,
      });
    }
    
    console.log('[Notifications] Channels created');
  } catch (error) {
    console.error('[Notifications] Failed to create channels:', error);
  }
};

/**
 * Schedule an appointment reminder notification
 * 
 * @param {Object} appointment - Appointment details
 * @param {number} reminderMinutes - Minutes before appointment to remind
 * @returns {Object} - Result with notification ID
 */
export const scheduleAppointmentReminder = async (appointment, reminderMinutes = 60) => {
  const isNative = await initNotificationPlugins();
  
  const {
    id,
    doctorName,
    appointmentDate,
    appointmentTime,
    specialty,
    reason,
  } = appointment;
  
  if (!appointmentDate) {
    return { success: false, error: 'No appointment date provided' };
  }
  
  // Calculate notification time
  const appointmentDateTime = new Date(appointmentDate);
  if (appointmentTime) {
    const [hours, minutes] = appointmentTime.split(':');
    appointmentDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10));
  }
  
  const notificationTime = new Date(appointmentDateTime.getTime() - reminderMinutes * 60 * 1000);
  
  // Don't schedule if time has passed
  if (notificationTime <= new Date()) {
    return { success: false, error: 'Reminder time has already passed' };
  }
  
  // Generate unique notification ID from appointment ID
  const notificationId = hashStringToNumber(id);
  
  // Build notification content (SECURITY: Generic message without sensitive medical info)
  const title = 'Appointment Reminder';
  const body = reminderMinutes >= 60
    ? `You have an appointment in ${Math.round(reminderMinutes / 60)} hour${reminderMinutes >= 120 ? 's' : ''}`
    : `You have an appointment in ${reminderMinutes} minutes`;
  
  if (isNative && LocalNotifications) {
    try {
      await LocalNotifications.schedule({
        notifications: [{
          id: notificationId,
          title,
          body,
          schedule: {
            at: notificationTime,
            allowWhileIdle: true,
          },
          channelId: NOTIFICATION_CHANNELS.APPOINTMENTS,
          smallIcon: 'ic_stat_notification',
          largeIcon: 'ic_launcher',
          actionTypeId: 'APPOINTMENT_ACTION',
          extra: {
            type: NOTIFICATION_TYPE.APPOINTMENT_REMINDER,
            appointmentId: id,
            url: `/medical/appointment/${id}`,
          },
          sound: 'default',
          attachments: null,
          group: 'appointments',
          groupSummary: false,
        }],
      });
      
      console.log(`[Notifications] Scheduled reminder for ${notificationTime.toISOString()}`);
      
      return {
        success: true,
        notificationId,
        scheduledFor: notificationTime.toISOString(),
      };
    } catch (error) {
      console.error('[Notifications] Failed to schedule:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Web fallback - store in localStorage for service worker to handle
  try {
    const scheduledReminders = JSON.parse(localStorage.getItem('scheduledReminders') || '[]');
    scheduledReminders.push({
      id: notificationId,
      appointmentId: id,
      title,
      body,
      scheduledFor: notificationTime.toISOString(),
      type: NOTIFICATION_TYPE.APPOINTMENT_REMINDER,
    });
    localStorage.setItem('scheduledReminders', JSON.stringify(scheduledReminders));
    
    return {
      success: true,
      notificationId,
      scheduledFor: notificationTime.toISOString(),
      webFallback: true,
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Schedule multiple reminders for an appointment (e.g., 1 day, 2 hours, 30 mins before)
 */
export const scheduleAppointmentReminders = async (appointment, reminderTimes = [1440, 120, 30]) => {
  const results = [];
  
  for (const minutes of reminderTimes) {
    const result = await scheduleAppointmentReminder(
      { ...appointment, id: `${appointment.id}_${minutes}` },
      minutes
    );
    results.push({ minutes, ...result });
  }
  
  return results;
};

/**
 * Cancel a scheduled notification
 */
export const cancelNotification = async (notificationId) => {
  const isNative = await initNotificationPlugins();
  
  if (isNative && LocalNotifications) {
    try {
      await LocalNotifications.cancel({
        notifications: [{ id: notificationId }],
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
  
  // Web fallback
  try {
    const scheduledReminders = JSON.parse(localStorage.getItem('scheduledReminders') || '[]');
    const filtered = scheduledReminders.filter(r => r.id !== notificationId);
    localStorage.setItem('scheduledReminders', JSON.stringify(filtered));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Cancel all reminders for an appointment
 */
export const cancelAppointmentReminders = async (appointmentId) => {
  const isNative = await initNotificationPlugins();
  
  if (isNative && LocalNotifications) {
    try {
      const pending = await LocalNotifications.getPending();
      const appointmentNotifs = pending.notifications.filter(n => 
        n.extra?.appointmentId === appointmentId || 
        n.extra?.appointmentId?.startsWith(appointmentId)
      );
      
      if (appointmentNotifs.length > 0) {
        await LocalNotifications.cancel({
          notifications: appointmentNotifs.map(n => ({ id: n.id })),
        });
      }
      return { success: true, cancelled: appointmentNotifs.length };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
  
  // Web fallback
  try {
    const scheduledReminders = JSON.parse(localStorage.getItem('scheduledReminders') || '[]');
    const filtered = scheduledReminders.filter(r => 
      !r.appointmentId?.startsWith(appointmentId)
    );
    const cancelled = scheduledReminders.length - filtered.length;
    localStorage.setItem('scheduledReminders', JSON.stringify(filtered));
    return { success: true, cancelled };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get all pending (scheduled) notifications
 */
export const getPendingNotifications = async () => {
  const isNative = await initNotificationPlugins();
  
  if (isNative && LocalNotifications) {
    try {
      const result = await LocalNotifications.getPending();
      return result.notifications;
    } catch (error) {
      console.error('[Notifications] Failed to get pending:', error);
      return [];
    }
  }
  
  // Web fallback
  try {
    return JSON.parse(localStorage.getItem('scheduledReminders') || '[]');
  } catch {
    return [];
  }
};

/**
 * Show an immediate notification
 */
export const showNotification = async (title, body, options = {}) => {
  const isNative = await initNotificationPlugins();
  
  if (isNative && LocalNotifications) {
    try {
      await LocalNotifications.schedule({
        notifications: [{
          id: options.id || Date.now(),
          title,
          body,
          channelId: options.channelId || NOTIFICATION_CHANNELS.REMINDERS,
          smallIcon: 'ic_stat_notification',
          extra: options.extra || {},
          sound: options.sound !== false ? 'default' : undefined,
        }],
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
  
  // Web fallback
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/logo192.png',
      tag: options.tag,
      data: options.extra,
    });
    return { success: true };
  }
  
  return { success: false, error: 'Notifications not available' };
};

/**
 * Set up notification action listeners
 */
export const setupNotificationListeners = async (onNotificationAction) => {
  const isNative = await initNotificationPlugins();
  
  if (isNative && LocalNotifications) {
    // Listen for notification taps
    await LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
      console.log('[Notifications] Action performed:', action);
      
      const extra = action.notification?.extra;
      if (extra?.url) {
        // Navigate to the URL
        window.location.href = extra.url;
      }
      
      if (onNotificationAction) {
        onNotificationAction(action);
      }
    });
    
    // Listen for when notifications are received while app is open
    await LocalNotifications.addListener('localNotificationReceived', (notification) => {
      console.log('[Notifications] Received while open:', notification);
    });
    
    console.log('[Notifications] Listeners set up');
  }
};

/**
 * Register for push notifications (for server-sent notifications)
 */
export const registerForPushNotifications = async () => {
  const isNative = await initNotificationPlugins();
  
  if (!isNative || !PushNotifications) {
    return { success: false, error: 'Push notifications not available' };
  }
  
  try {
    // Request permission
    const permissionResult = await PushNotifications.requestPermissions();
    if (permissionResult.receive !== 'granted') {
      return { success: false, error: 'Permission denied' };
    }
    
    // Register with APNs/FCM
    await PushNotifications.register();
    
    // Get device token
    return new Promise((resolve) => {
      PushNotifications.addListener('registration', (token) => {
        console.log('[Push] Registered with token:', token.value);
        resolve({ success: true, token: token.value });
      });
      
      PushNotifications.addListener('registrationError', (error) => {
        console.error('[Push] Registration failed:', error);
        resolve({ success: false, error: error.error });
      });
    });
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Helper to convert string ID to numeric notification ID
 */
const hashStringToNumber = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Initialize the notification service
 */
export const initNotificationService = async () => {
  const isNative = await initNotificationPlugins();
  
  if (isNative) {
    await setupNotificationChannels();
    await setupNotificationListeners();
    console.log('[Notifications] Service initialized');
  }
  
  return { isNative };
};

export default {
  isNativeNotificationAvailable,
  requestNotificationPermission,
  checkNotificationPermission,
  setupNotificationChannels,
  scheduleAppointmentReminder,
  scheduleAppointmentReminders,
  cancelNotification,
  cancelAppointmentReminders,
  getPendingNotifications,
  showNotification,
  setupNotificationListeners,
  registerForPushNotifications,
  initNotificationService,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_TYPE,
};
